import { reportAppHealthError } from './webAnalyticsReporter'

/**
 * Typed app-health error vocabulary + emitter (`metro-health-events.md`
 * §2/§3, the LOCKED cross-platform contract — iOS/Android/Web report the
 * SAME `id` codes + metadata vocab). This module is the web call-site facade:
 * the `id` codes are constants (never interpolated — §3), the metadata keys
 * are closed unions (§2), so a typo or an off-vocabulary value is a compile
 * error rather than a shattered AppMetrica group.
 *
 * The raw sink (`reportAppHealthError` in `webAnalyticsReporter.ts`) emits the
 * reserved `app_health_error` event to Amplitude/GA4; this layer only
 * shapes + type-guards the payload.
 *
 * Web scope (§6): web has no AppMetrica Errors report (no native reportError);
 * health rolls up in Amplitude. Web has no paywall ([[project_metro_web_no_paywall]])
 * → the paywall / purchase / entitlement / restore codes are **n/a on web**
 * (not defined here). Web has no remote-config (no Varioqub on web) →
 * `remote_config_fetch_failed` is **n/a**. Location reporter is iOS-only →
 * `cl_location_error` is **n/a** on web. The codes below are exactly the web-
 * applicable subset.
 */

/** §2 `domain` — the coarse subsystem facet (web-applicable subset). */
export type HealthDomain =
  | 'network'
  | 'geometry'
  | 'routing'
  | 'status'
  | 'persistence'
  | 'deeplink'
  | 'sdk_init'

/** §2 `error_kind` — the stable failure mode (reuses the routing enum). */
export type HealthErrorKind =
  | 'timeout'
  | 'offline'
  | 'http_5xx'
  | 'http_4xx'
  | 'decode'
  | 'empty'
  | 'vendor'
  | 'not_found'
  | 'gl_init'
  | 'parse'
  | 'unknown'

/**
 * §2 `severity` — `warning` (degraded but recovered — a fallback fired) vs.
 * `error` (the feature failed for the user). Drives alert routing.
 */
export type HealthSeverity = 'warning' | 'error'

/** §2 `endpoint` — the logical network endpoint (NOT the URL; no PII). */
export type HealthEndpoint = 'search' | 'routing' | 'status' | 'disruptions' | 'schema'

/**
 * §2 `fallback` — for `severity=warning`, what we degraded to.
 *
 * `skip` = the write was abandoned and nothing retries (emitted here by
 * `route_key_slug_missing`). The §2 table also defines `empty` — the feature
 * is live but permanently sees no data — which belongs to
 * `persistence_store_load_failed`, a code web does not yet emit: web's
 * localStorage reads degrade per-key rather than failing the whole store
 * open. Add the value here when web grows a store that can fail to open.
 */
export type HealthFallback = 'cache' | 'offline' | 'default' | 'bundled' | 'skip'

/**
 * §3 error `id` codes — the AppMetrica grouping keys, web-applicable subset.
 * Stable, low-cardinality, never interpolated. Paywall / purchase /
 * entitlement / restore / remote_config / location codes are intentionally
 * absent (n/a on web — see module doc).
 */
export const HEALTH_ID = {
  /** §3 network — generic API failure, faceted by `endpoint` + `error_kind`. */
  apiRequestFailed: 'api_request_failed',
  /** §3 geometry — MMTG geometry load/parse failure. */
  mmtgLoadFailed: 'mmtg_load_failed',
  /** §3 geometry — WebGL2 context init failure (first-frame blocker). */
  mapGlInitFailed: 'map_gl_init_failed',
  /** §3 geometry — station/line schema decode failure. */
  schemaDecodeFailed: 'schema_decode_failed',
  /** §3 routing — routing graph failed to load (NOT a no-route, which is the
   *  `route_resolve_failed` product event). */
  routingGraphLoadFailed: 'routing_graph_load_failed',
  /** §3 sdk_init — an analytics SDK failed to initialize. */
  sdkInitFailed: 'sdk_init_failed',
  /** §3 persistence — a localStorage / IndexedDB write failed; cache degraded. */
  persistenceWriteFailed: 'persistence_write_failed',
  /**
   * §3 persistence — a route could not be persisted to history/favourites
   * because a station carried no rename-stable `slug` (`fallback=skip`). The
   * client drops the write rather than storing a half-key, so this event is
   * the ONLY signal; a rising rate means schema drift, not user behaviour.
   * Mirrors iOS `MetroHealthError.Code.routeKeySlugMissing`.
   */
  routeKeySlugMissing: 'route_key_slug_missing',
  /** §3 deeplink — a deep link arrived but resolved to nothing. */
  deeplinkResolveFailed: 'deeplink_resolve_failed',
} as const

export type HealthId = (typeof HEALTH_ID)[keyof typeof HEALTH_ID]

/**
 * §2 standard metadata + the optional context keys. `domain` + `severity` are
 * always present; `error_kind` is present whenever the failure mode is known.
 * Optional keys are OMITted on the wire when absent (the sink drops nullish).
 */
export interface HealthMetadata {
  readonly domain: HealthDomain
  readonly severity: HealthSeverity
  readonly errorKind?: HealthErrorKind
  readonly endpoint?: HealthEndpoint
  readonly statusCode?: number
  readonly latencyMs?: number
  readonly fallback?: HealthFallback
  /** Free-form extra context (e.g. `sdk`, `scheme`) — kept small + non-PII. */
  readonly extra?: Record<string, string | number | boolean>
}

/**
 * Emit a web app-health error with the typed §2/§3 contract. Maps the camelCase
 * input onto the snake_case wire keys the cross-platform schema uses, then
 * forwards through the fail-safe sink. `message` is the human-readable detail
 * (may interpolate dynamic values — unlike `id`).
 */
export function reportHealthError(id: HealthId, message: string, meta: HealthMetadata): void {
  const { domain, severity, errorKind, endpoint, statusCode, latencyMs, fallback, extra } = meta
  reportAppHealthError(id, message, {
    domain,
    severity,
    error_kind: errorKind,
    endpoint,
    status_code: statusCode,
    latency_ms: latencyMs,
    fallback,
    ...extra,
  })
}

/**
 * Classify a thrown fetch/network value into the §2 `error_kind` + an optional
 * HTTP `status_code`. Reused by the network call sites (status / disruptions /
 * routing) so the `error_kind` mapping stays consistent. A `Response`-shaped
 * throw maps 5xx→`http_5xx`, 4xx→`http_4xx`; a `TypeError`/abort →
 * `offline`/`timeout`; anything else → `unknown`.
 */
export function classifyNetworkError(err: unknown): {
  errorKind: HealthErrorKind
  statusCode?: number
} {
  // A fetch that resolved with a non-ok Response (callers throw it on !ok).
  if (typeof err === 'object' && err !== null && 'status' in err) {
    const status = Number((err as { status: unknown }).status)
    if (Number.isFinite(status)) {
      if (status >= 500) return { errorKind: 'http_5xx', statusCode: status }
      if (status >= 400) return { errorKind: 'http_4xx', statusCode: status }
    }
  }
  if (err instanceof DOMException && err.name === 'AbortError') return { errorKind: 'timeout' }
  // `fetch` rejects with a TypeError on a network-layer failure (offline, DNS,
  // CORS) — the closest web analogue to the mobile `offline` kind.
  if (err instanceof TypeError) return { errorKind: 'offline' }
  return { errorKind: 'unknown' }
}
